"use client"

import { useState, useEffect, useCallback, useRef } from "react"
import styles from "./Gallery.module.css"
import gallerydata from "./Gallerydata"
import Lightbox from "react-image-lightbox"
import "react-image-lightbox/style.css"
import { Helmet } from "react-helmet-async"

const months = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
]

// Deduplicate by image src
const uniqueGallery = gallerydata.filter(
  (item, idx, arr) => arr.findIndex((x) => x.image === item.image) === idx
)

const Gallery = () => {
  const [selectedMonth, setSelectedMonth] = useState("All")
  const [lightboxIndex, setLightboxIndex] = useState(0)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [visibleItems, setVisibleItems] = useState({})
  const [pageReady, setPageReady] = useState(false)
  const observerRef = useRef(null)

  const filteredGallery = uniqueGallery.filter((item) => {
    const yearMatch = item.year === 2025
    const monthMatch = selectedMonth === "All" || item.month === selectedMonth
    return yearMatch && monthMatch
  })

  // Page ready after short delay — no more blocking loader
  useEffect(() => {
    const t = setTimeout(() => setPageReady(true), 200)
    return () => clearTimeout(t)
  }, [])

  // Reset visible items when filter changes
  useEffect(() => {
    setVisibleItems({})
  }, [selectedMonth])

  // IntersectionObserver for lazy fade-in of each card
  const setupObserver = useCallback(() => {
    if (observerRef.current) observerRef.current.disconnect()
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const key = entry.target.dataset.key
            setVisibleItems((prev) => ({ ...prev, [key]: true }))
            observerRef.current.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    )
    document.querySelectorAll("[data-key]").forEach((el) => {
      observerRef.current.observe(el)
    })
  }, [])

  useEffect(() => {
    setupObserver()
    return () => observerRef.current?.disconnect()
  }, [filteredGallery, setupObserver])

  const openLightbox = (index) => {
    setLightboxIndex(index)
    setLightboxOpen(true)
  }

  // Available months (only those that have images)
  const availableMonths = months.filter((m) =>
    uniqueGallery.some((item) => item.month === m && item.year === 2025)
  )

  return (
    <>
      <Helmet>
        <title>School Gallery | Hillgreen High School</title>
        <meta
          name="description"
          content="Explore Hillgreen High School's gallery showcasing events, celebrations, and memorable moments throughout the year."
        />
        <link rel="canonical" href="https://www.hillgreenhighschool.in/gallery" />
        <meta property="og:title" content="School Gallery | Hillgreen High School" />
      </Helmet>

      <div className={`${styles.gallery} ${pageReady ? styles.ready : ""}`}>

        {/* ── Hero ── */}
        <div className={styles.gallery_head}>
          <div className={styles.hero_gradient}></div>
          <div className={styles.hero_particles}>
            {[...Array(6)].map((_, i) => (
              <span key={i} className={styles.particle}></span>
            ))}
          </div>
          <div className={styles.gallery_head_text}>
            <span className={styles.hero_badge}>📸 Our Memories</span>
            <h1>School Gallery</h1>
            <p>Capturing precious moments, events &amp; achievements</p>
            <div className={styles.hero_stats}>
              <div className={styles.stat}>
                <strong>{uniqueGallery.length}+</strong>
                <span>Photos</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Filter Bar ── */}
        <div className={styles.controls_section}>
          <div className={styles.month_selector}>
            <div className={styles.filter_label}>
              <span className={styles.filter_icon}>🗓️</span>
              <h2>Filter by Month</h2>
            </div>
            <div className={styles.month_buttons}>
              <button
                className={`${styles.month_btn} ${selectedMonth === "All" ? styles.active : ""}`}
                onClick={() => setSelectedMonth("All")}
              >
                All Photos
                <span className={styles.count_badge}>{uniqueGallery.filter(i => i.year===2025).length}</span>
              </button>
              {availableMonths.map((month) => {
                const count = uniqueGallery.filter(i => i.month === month && i.year === 2025).length
                return (
                  <button
                    key={month}
                    className={`${styles.month_btn} ${selectedMonth === month ? styles.active : ""}`}
                    onClick={() => setSelectedMonth(month)}
                  >
                    {month}
                    <span className={styles.count_badge}>{count}</span>
                  </button>
                )
              })}
            </div>
          </div>
        </div>

        {/* ── Results Bar ── */}
        <div className={styles.results_bar}>
          <span>
            Showing <strong>{filteredGallery.length}</strong> photos
            {selectedMonth !== "All" ? ` for ${selectedMonth}` : ""}
          </span>
        </div>

        {/* ── Gallery Grid ── */}
        <div className={styles.gallery_main}>
          {filteredGallery.length > 0 ? (
            filteredGallery.map((item, index) => {
              const key = `${item.month}-${index}`
              const isVisible = visibleItems[key]
              return (
                <div
                  key={key}
                  data-key={key}
                  className={`${styles.gallery_item} ${isVisible ? styles.item_visible : ""}`}
                  onClick={() => openLightbox(index)}
                  style={{ "--delay": `${(index % 6) * 60}ms` }}
                >
                  <div className={styles.image_container}>
                    <img
                      src={item.image}
                      alt={`${item.month} - Photo ${index + 1}`}
                      loading="lazy"
                      decoding="async"
                    />
                    <div className={styles.image_overlay}>
                      <div className={styles.overlay_top}>
                        <span className={styles.image_month}>{item.month}</span>
                      </div>
                      <div className={styles.overlay_bottom}>
                        <div className={styles.view_btn}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                          </svg>
                          View Full
                        </div>
                      </div>
                    </div>
                    <div className={styles.item_number}>#{index + 1}</div>
                  </div>
                </div>
              )
            })
          ) : (
            <div className={styles.no_images}>
              <div className={styles.no_images_content}>
                <span className={styles.no_images_icon}>📷</span>
                <h3>No photos yet</h3>
                <p>No images available for {selectedMonth}</p>
                <button
                  className={styles.reset_btn}
                  onClick={() => setSelectedMonth("All")}
                >
                  View All Photos
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Lightbox ── */}
      {lightboxOpen && filteredGallery.length > 0 && (
        <Lightbox
          mainSrc={filteredGallery[lightboxIndex].image}
          nextSrc={filteredGallery[(lightboxIndex + 1) % filteredGallery.length].image}
          prevSrc={filteredGallery[(lightboxIndex + filteredGallery.length - 1) % filteredGallery.length].image}
          onCloseRequest={() => setLightboxOpen(false)}
          onMovePrevRequest={() =>
            setLightboxIndex((lightboxIndex + filteredGallery.length - 1) % filteredGallery.length)
          }
          onMoveNextRequest={() =>
            setLightboxIndex((lightboxIndex + 1) % filteredGallery.length)
          }
          imageTitle={`${filteredGallery[lightboxIndex].month} — ${lightboxIndex + 1} / ${filteredGallery.length}`}
        />
      )}
    </>
  )
}

export default Gallery
